import { useEffect, useRef, useState } from 'react'
import * as signalR from '@microsoft/signalr'
import { startConnection, stopConnection } from '@/services/signalr'
import { getSuggestedQuestions } from '@/services/api'
import { useChatStore } from '@/store/chatStore'

interface UseSignalRResult {
  connection: signalR.HubConnection | null
  isConnected: boolean
}

export function useSignalR(apiUrl: string, token: string): UseSignalRResult {
  const connectionRef = useRef<signalR.HubConnection | null>(null)
  const [isConnected, setIsConnected] = useState(false)

  useEffect(() => {
    // Don't attempt connection until token is available
    if (!token) return

    let cancelled = false

    async function connect() {
      try {
        const conn = await startConnection(apiUrl, token)
        if (cancelled) {
          await conn.stop()
          return
        }

        connectionRef.current = conn
        setIsConnected(true)

        conn.onreconnecting(() => setIsConnected(false))
        conn.onreconnected(() => setIsConnected(true))
        conn.onclose(() => setIsConnected(false))

        // Listen for intro message
        conn.on('ReceiveIntro', (message: string) => {
          if (message) useChatStore.getState().setIntroMessage(message)
        })

        // Listen for follow-up suggestion chips after each AI answer
        conn.on('ReceiveSuggestions', (suggestions: string[]) => {
          if (Array.isArray(suggestions) && suggestions.length > 0)
            useChatStore.getState().setFollowUpSuggestions(suggestions)
        })

        const config = useChatStore.getState().config
        if (config?.domainId) {
          try {
            await conn.invoke('GetIntroMessage', config.domainId)
          } catch (err) {
            console.warn('[Widget] GetIntroMessage failed:', err)
          }

          // Fetch predefined questions (fire-and-forget, best-effort)
          getSuggestedQuestions(config.domainId)
            .then((qs) => { if (qs.length > 0) useChatStore.getState().setSuggestedQuestions(qs) })
            .catch(() => {})
        }
      } catch {
        setIsConnected(false)
      }
    }

    connect()

    return () => {
      cancelled = true
      stopConnection()
      setIsConnected(false)
    }
  }, [apiUrl, token]) // Re-runs when token becomes available after auth

  return {
    connection: connectionRef.current,
    isConnected,
  }
}